import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { BlockUIModule } from '@openng/optimus-ui/blockui';
import { CheckboxModule } from '@openng/optimus-ui/checkbox';
import { DividerModule } from '@openng/optimus-ui/divider';
import { firstValueFrom, Subscription } from 'rxjs';
import { MOBILE_CONTROLLER_NOTES_SEARCH_CONTAINER } from 'src/app/constants/common.constants';
import { LogEntryComponent } from 'src/app/core/components/log-entry.component';
import { ProgressSpinnerComponent } from 'src/app/core/components/progress-spinner.component';
import { DetailsContainer } from 'src/app/core/containers/details-container';
import { TabService } from 'src/app/core/services/tab.service';
import { ControllerNote } from 'src/app/model/controllerNotes/controllerNote';
import { ControllerNotesApiService } from 'src/app/services/api/controller-notes-api.service';
import { BreadCrumbBuilderService } from 'src/app/services/builder/breadcrumb-builder.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { emptyNote } from 'src/app/utils/utils';

@Component({
  selector: 'app-mobile-note-form',
  imports: [
    DatePipe,
    CheckboxModule,
    TranslateModule,
    ReactiveFormsModule,
    DividerModule,
    LogEntryComponent,
    BlockUIModule,
    ProgressSpinnerComponent,
  ],
  templateUrl: './mobile-note-form.component.html',
  styleUrl: './mobile-note-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileNoteFormComponent
  extends DetailsContainer
  implements OnInit, OnDestroy
{
  protected _fb = inject(FormBuilder);
  protected _controllerNotesApi = inject(ControllerNotesApiService);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _breadCrumbService = inject(BreadCrumbBuilderService);

  pageLoading = signal(true);
  controllerNote: WritableSignal<ControllerNote> = signal({ ...emptyNote });
  category = computed(() => {
    const note = this.controllerNote();
    return note.category;
  });
  noteFormGroup: FormGroup;

  breadCrumbSub: Subscription;

  constructor() {
    super();

    this.noteFormGroup = this._fb.group({
      typeAOR: this._fb.group({
        maop: [false],
        falseAlarm: [false],
        abnormalOperation: [false],
        includeInTurnover: [false],
        calloutInitiated: [false],
        maintenance: [false],
      }),
    });
    this.noteFormGroup.disable();

    this.breadCrumbSub =
      this._breadCrumbService.breadCrumbSelectedEvent.subscribe(
        (breadCrumb) => {
          this._breadCrumbService.removeBreadCrumb(breadCrumb.item);
          const tab = this._tabService.buildNewTab(
            MOBILE_CONTROLLER_NOTES_SEARCH_CONTAINER,
            true
          );
          this._tabService.setMobileTab(tab);
        }
      );
  }

  async ngOnInit(): Promise<void> {
    this.getControllerNote();

    const screenName = await firstValueFrom(
      this._translateService.get('CRM.SCREEN.NOTE_FORM')
    );
    this._tabService.updateActiveTabLabel(screenName);
    this.screenName = screenName;
  }

  ngOnDestroy(): void {
    this.breadCrumbSub.unsubscribe();
  }

  getControllerNote() {
    this.pageLoading.set(true);
    this._controllerNotesApi.getControllerNote(this.id, false).subscribe({
      next: (data) => {
        this.controllerNote.set(data);
        this.initializePage();
      },
      error: (error) => {
        this.pageLoading.set(false);
        this._logAndMessage.errorLogOnly(error);
        this._logAndMessage.translateToErrorAlert({
          bodyKey: 'CRM.MESSAGES.ERROR.GET_CONTROLLER_NOTE',
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
        });

        const index = this._tabService.getActiveIndex();
        this._tabService.closeTab(index);
      },
    });
  }

  initializePage() {
    const note = this.controllerNote();
    this._breadCrumbService.addBreadCrumb({
      label: note.AORName + ' - ' + note.facilityOrPipelineNum,
      routerLink: null,
    });

    this.noteFormGroup.patchValue({
      typeAOR: {
        maop: note.maop,
        falseAlarm: note.falseAlarm,
        abnormalOperation: note.abnormalOperation,
        includeInTurnover: note.addToTurnover,
        calloutInitiated: note.calloutInitiated,
        maintenane: note.maintenance,
      },
    });
    this.pageLoading.set(false);
  }
}
